import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* 返回按钮 */}
        <div className="mb-8">
          <Link
            to="/auth/register"
            className="inline-flex items-center text-emerald-600 hover:text-emerald-700 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            返回注册
          </Link>
        </div>

        {/* 页面标题 */}
        <div className="bg-white rounded-2xl shadow-lg border border-emerald-100 p-8 md:p-12">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            隐私政策
          </h1>
          <p className="text-gray-500 mb-8">最后更新时间：2025年1月</p>

          <div className="prose prose-emerald max-w-none">
            {/* 引言 */}
            <section className="mb-8">
              <p className="text-gray-700 leading-relaxed">
                离岸绿色债券直通车（Offshore Green Bond
                Express）平台（以下简称"我们"或"本平台"）非常重视用户的隐私保护。本隐私政策说明了我们如何收集、使用、存储和保护您的个人信息。
              </p>
            </section>

            {/* 第1条 */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                1. 我们收集的信息
              </h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                在您使用本平台服务时，我们可能收集以下类型的信息：
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-5">
                1.1 您主动提供的信息
              </h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>注册信息：公司/机构名称、联系人姓名、邮箱地址、联系电话</li>
                <li>账户信息：登录凭证、机构类型、用户角色</li>
                <li>业务信息：项目资料、问卷回答、上传的文档</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-5">
                1.2 自动收集的信息
              </h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>设备信息：IP地址、浏览器类型、操作系统</li>
                <li>使用信息：访问时间、页面浏览记录、操作日志</li>
                <li>Cookie及类似技术收集的信息</li>
              </ul>
            </section>

            {/* 第2条 */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                2. 信息的使用目的
              </h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                我们收集和使用您的个人信息用于以下目的：
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>提供、维护和改进平台服务</li>
                <li>处理您的注册、身份验证和账户管理</li>
                <li>向您发送服务相关通知和更新</li>
                <li>分析平台使用情况，优化用户体验</li>
                <li>防止欺诈、滥用和安全威胁</li>
                <li>履行法律义务和合规要求</li>
              </ul>
            </section>

            {/* 第3条 */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                3. 信息的共享与披露
              </h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                我们承诺不会出售或出租您的个人信息。在以下情况下，我们可能会共享您的信息：
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>
                  <strong>业务合作伙伴：</strong>
                  为完成债券发行流程，与相关的认证机构、咨询服务商等共享必要信息（需获得您的明确同意）
                </li>
                <li>
                  <strong>服务提供商：</strong>
                  向为我们提供技术支持、数据分析等服务的第三方提供商共享信息
                </li>
                <li>
                  <strong>法律要求：</strong>
                  根据法律法规、诉讼或政府要求披露信息
                </li>
                <li>
                  <strong>企业交易：</strong>
                  在合并、收购或资产转让时，您的信息可能会被转移（我们会提前通知您）
                </li>
              </ul>
            </section>

            {/* 第4条 */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                4. 数据安全
              </h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                我们采取以下措施保护您的个人信息安全：
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>使用加密技术（HTTPS/SSL）保护数据传输</li>
                <li>对敏感数据进行加密存储</li>
                <li>实施严格的访问控制和身份验证机制</li>
                <li>定期进行安全审计和漏洞扫描</li>
                <li>员工接受数据保护培训，签署保密协议</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                尽管我们采取了上述措施，但无法保证信息的绝对安全。请您妥善保管登录凭证，避免与他人共享。
              </p>
            </section>

            {/* 第5条 */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                5. 数据保留
              </h2>
              <p className="text-gray-700 leading-relaxed">
                我们仅在实现收集目的所需的期限内保留您的个人信息，除非法律要求或允许更长的保留期。当信息不再需要时，我们将采取合理措施删除或匿名化处理。
              </p>
            </section>

            {/* 第6条 */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                6. 您的权利
              </h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                根据适用的数据保护法律，您拥有以下权利：
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>
                  <strong>访问权：</strong>
                  请求获取我们持有的关于您的个人信息副本
                </li>
                <li>
                  <strong>更正权：</strong>
                  要求更正不准确或不完整的个人信息
                </li>
                <li>
                  <strong>删除权：</strong>
                  在特定情况下要求删除您的个人信息
                </li>
                <li>
                  <strong>限制处理权：</strong>
                  要求限制对您个人信息的处理
                </li>
                <li>
                  <strong>数据可携权：</strong>
                  请求以结构化、常用格式获取您的数据
                </li>
                <li>
                  <strong>反对权：</strong>
                  反对我们处理您的个人信息
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                如需行使上述权利，请通过本政策末尾的联系方式与我们联系。
              </p>
            </section>

            {/* 第7条 */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                7. Cookie 和跟踪技术
              </h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                我们使用Cookie和类似技术来改善用户体验、分析平台使用情况。您可以通过浏览器设置管理Cookie，但这可能影响部分功能的使用。
              </p>
              <p className="text-gray-700 leading-relaxed">
                我们使用的Cookie类型包括：必要Cookie（维持基本功能）、功能Cookie（记住您的偏好设置）、分析Cookie（了解使用情况）。
              </p>
            </section>

            {/* 第8条 */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                8. 第三方链接
              </h2>
              <p className="text-gray-700 leading-relaxed">
                本平台可能包含指向第三方网站的链接。我们不对这些第三方网站的隐私做法负责。建议您查阅这些网站的隐私政策。
              </p>
            </section>

            {/* 第9条 */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                9. 儿童隐私
              </h2>
              <p className="text-gray-700 leading-relaxed">
                本平台不面向18岁以下的未成年人。我们不会有意收集儿童的个人信息。如果您发现我们收集了儿童的信息，请立即联系我们，我们将及时删除。
              </p>
            </section>

            {/* 第10条 */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                10. 隐私政策的更新
              </h2>
              <p className="text-gray-700 leading-relaxed">
                我们可能会不时更新本隐私政策。重大变更将通过平台通知或电子邮件通知您。继续使用平台即表示您接受更新后的政策。
              </p>
            </section>

            {/* 第11条 */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                11. 联系我们
              </h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                如对本隐私政策有任何疑问、意见或投诉，请通过以下方式联系我们：
              </p>
              <ul className="list-none space-y-2 text-gray-700">
                <li>电子邮件：marketing@bravowhale.com</li>
                <li>客服电话：+86 21 5077 7029</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                我们将在收到您的请求后30个工作日内回复。
              </p>
            </section>

            {/* 生效日期 */}
            <section className="mt-12 pt-8 border-t border-gray-200">
              <p className="text-gray-600 text-sm">
                本隐私政策自2026年1月1日起生效。您可以随时在平台上查看最新版本的隐私政策。
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
